import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useSiteSettings, useUpdateSiteSettings, api } from '@wordpuppi/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

export const Route = createFileRoute('/sites/$siteSlug/settings/email')({
  component: EmailSettingsPage,
});

// SMTP host/port presets. 'custom' leaves the fields untouched so the user can
// enter their own server.
const PROVIDER_PRESETS: Record<string, { host: string; port: string }> = {
  postmark: { host: 'smtp.postmarkapp.com', port: '587' },
  sendgrid: { host: 'smtp.sendgrid.net', port: '587' },
  mandrill: { host: 'smtp.mandrillapp.com', port: '587' },
  brevo: { host: 'smtp-relay.brevo.com', port: '587' },
};

function EmailSettingsPage() {
  const { siteSlug } = Route.useParams();
  const { data: settingsData, isLoading } = useSiteSettings(siteSlug);
  const updateSettings = useUpdateSiteSettings(siteSlug);

  const [provider, setProvider] = useState('custom');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');

  // Test-before-save gate (AB#615): Save stays disabled until the CURRENT field
  // values pass a live test. We stamp the values that passed; any later edit
  // changes the signature and re-disables Save.
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'failed'>('idle');
  const [testError, setTestError] = useState('');
  const [passedSig, setPassedSig] = useState<string | null>(null);

  const currentSig = JSON.stringify([
    smtpHost,
    smtpPort,
    smtpUsername,
    smtpPassword,
    fromEmail,
    fromName,
    replyTo,
  ]);
  const testPassed = passedSig !== null && passedSig === currentSig;

  useEffect(() => {
    if (settingsData?.data?.email_provider) {
      const ep = settingsData.data.email_provider;
      const host = ep.smtp_host || '';
      setSmtpHost(host);
      setSmtpPort(String(ep.smtp_port || 587));
      setSmtpUsername(ep.smtp_username || '');
      setFromEmail(ep.from_email || '');
      setFromName(ep.from_name || '');
      setReplyTo(ep.reply_to || '');
      // AB#615: the provider choice is NOT persisted — it only ever seeds the
      // host/port. Hydration restored every field except this one, so `provider`
      // kept its useState('custom') default and a saved Postmark config read
      // back as "Custom" on every reload. Derive it from the stored host (the
      // field that actually identifies the provider) instead of adding another
      // piece of state to keep in sync.
      setProvider(
        Object.keys(PROVIDER_PRESETS).find((k) => PROVIDER_PRESETS[k].host === host) ?? 'custom',
      );
    }
  }, [settingsData]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const providerBody = () => ({
    smtp_host: smtpHost || undefined,
    smtp_port: smtpPort ? parseInt(smtpPort, 10) : undefined,
    smtp_username: smtpUsername || undefined,
    smtp_password: smtpPassword || undefined,
    from_email: fromEmail || undefined,
    from_name: fromName || undefined,
    reply_to: replyTo || undefined,
  });

  const handleTest = async () => {
    setTestStatus('testing');
    setTestError('');
    try {
      // Password may be omitted → backend falls back to the stored one.
      const res = await api.post<{ ok: boolean; error?: string }>(
        `/v1/sites/${siteSlug}/email/test`,
        providerBody(),
      );
      if (!res.ok) throw new Error(res.error || 'Email test failed');
      setPassedSig(currentSig);
      setTestStatus('idle');
      toast.success('Test email sent');
    } catch (err) {
      setTestStatus('failed');
      setTestError(err instanceof Error ? err.message : 'Email test failed');
    }
  };

  const handleSaveEmailProvider = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateSettings.mutateAsync({
        email_provider: providerBody(),
      });
      setSmtpPassword('');
      toast.success('Email provider settings updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update email settings');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Email Provider</CardTitle>
          <CardDescription>
            Configure SMTP settings for sending form notifications, newsletters, and other emails
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveEmailProvider} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emailProvider">Provider</Label>
              <Select
                value={provider}
                onValueChange={(v) => {
                  setProvider(v);
                  const preset = PROVIDER_PRESETS[v];
                  if (preset) {
                    setSmtpHost(preset.host);
                    setSmtpPort(preset.port);
                  }
                }}
              >
                <SelectTrigger id="emailProvider" className="max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="postmark">Postmark</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                  <SelectItem value="mandrill">Mailchimp / Mandrill</SelectItem>
                  <SelectItem value="brevo">Brevo</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Pick a provider to prefill the host and port, or choose Custom
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtpHost">SMTP Host</Label>
                <Input
                  id="smtpHost"
                  type="text"
                  value={smtpHost}
                  onChange={(e) => setSmtpHost(e.target.value)}
                  placeholder="smtp.example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtpPort">SMTP Port</Label>
                <Input
                  id="smtpPort"
                  type="number"
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(e.target.value)}
                  placeholder="587"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtpUsername">Username</Label>
                <Input
                  id="smtpUsername"
                  type="text"
                  value={smtpUsername}
                  onChange={(e) => setSmtpUsername(e.target.value)}
                  placeholder="user@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtpPassword">Password</Label>
                <Input
                  id="smtpPassword"
                  type="password"
                  value={smtpPassword}
                  onChange={(e) => setSmtpPassword(e.target.value)}
                  placeholder={settingsData?.data?.email_provider?.smtp_host ? '••••••••' : 'Enter password'}
                />
                {settingsData?.data?.email_provider?.smtp_host && (
                  <p className="text-xs text-muted-foreground">
                    Leave blank to keep existing password
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fromEmail">From Email</Label>
                <Input
                  id="fromEmail"
                  type="email"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  placeholder="noreply@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fromName">From Name</Label>
                <Input
                  id="fromName"
                  type="text"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder="My Site"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="replyTo">Reply-To (optional)</Label>
              <Input
                id="replyTo"
                type="email"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                placeholder="hello@example.com"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleTest}
                disabled={testStatus === 'testing'}
              >
                {testStatus === 'testing' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending test...
                  </>
                ) : (
                  'Send test email'
                )}
              </Button>
              <Button type="submit" disabled={!testPassed || updateSettings.isPending}>
                {updateSettings.isPending ? 'Saving...' : 'Save Email Settings'}
              </Button>
              {testPassed && (
                <span className="text-sm text-success flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" />
                  Test passed
                </span>
              )}
              {testStatus === 'failed' && (
                <span className="text-sm text-destructive flex items-center gap-1.5">
                  <XCircle className="h-4 w-4 shrink-0" />
                  {testError}
                </span>
              )}
            </div>
            {!testPassed && testStatus !== 'failed' && (
              <p className="text-sm text-muted-foreground">
                Send a passing test email to enable Save.
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
